import React from "react";
import TeamLogo from "./TeamLogo";
import { formatTeamScore, hasLiveScores } from "../../utility/matchScore";

/**
 * Sport-agnostic live/upcoming/ended score panel.
 * Uses API `scores` array — no hardcoded cricket UI.
 *
 * variant="compact" — inline badge + score for phone board cards (no TV artwork).
 */
export default function MatchLiveScorePanel({
  game,
  phase,
  variant = "tv",
  showPhaseTitle = true,
  showScoreLogos = true,
}) {
  if (!game) return null;

  const renderScoreLine = (homeFallback, awayFallback) => {
    if (!showScoreLogos) {
      return (
        <div className="scoreOverviewPoint scoreOverviewPoint--numeric fs13">
          <span>{formatTeamScore(game, game.home_team, homeFallback)}</span>
          <span className="scoreOverviewPoint__colon">:</span>
          <span>{formatTeamScore(game, game.away_team, awayFallback)}</span>
        </div>
      );
    }
    return (
      <div className="scoreOverviewPoint fs13">
        <p>
          <TeamLogo
            teamName={game.home_team}
            logoUrl={game.home_team_logo}
            size="sm"
            alt={game.home_team}
          />
          <span>{formatTeamScore(game, game.home_team, homeFallback)}</span>
        </p>
        <span>:</span>
        <p>
          <span>{formatTeamScore(game, game.away_team, awayFallback)}</span>
          <TeamLogo
            teamName={game.away_team}
            logoUrl={game.away_team_logo}
            size="sm"
            alt={game.away_team}
          />
        </p>
      </div>
    );
  };

  if (variant === "compact") {
    if (phase === "upcoming") return null;

    if (phase === "live") {
      return (
        <div className="match-live-score-compact" aria-live="polite">
          <span className="match-live-score-compact__badge">Live</span>
          {hasLiveScores(game) ? (
            <span className="match-live-score-compact__values">
              {formatTeamScore(game, game.home_team, "0")}
              <span className="match-live-score-compact__sep">–</span>
              {formatTeamScore(game, game.away_team, "0")}
            </span>
          ) : (
            <span className="match-live-score-compact__muted">Score unavailable</span>
          )}
        </div>
      );
    }

    if (phase === "ended") {
      if (!hasLiveScores(game)) {
        return (
          <div className="match-live-score-compact match-live-score-compact--final">
            <span className="match-live-score-compact__badge">Final</span>
          </div>
        );
      }
      return (
        <div className="match-live-score-compact match-live-score-compact--final" aria-live="polite">
          <span className="match-live-score-compact__badge">Final</span>
          <span className="match-live-score-compact__values">
            {formatTeamScore(game, game.home_team, "—")}
            <span className="match-live-score-compact__sep">–</span>
            {formatTeamScore(game, game.away_team, "—")}
          </span>
        </div>
      );
    }

    return null;
  }

  if (phase === "live") {
    return (
      <div className="tvContent">
        {showPhaseTitle ? <p className="fs14 titleText">Live</p> : null}
        {hasLiveScores(game) ? (
          renderScoreLine("0", "0")
        ) : (
          // Odds API only returns in-play scores for selected sports (NBA, NFL,
          // soccer, …). Cricket / NPB / etc. stay null — do not imply a number
          // is about to appear.
          <span className="fs15 infoText">Live — score unavailable</span>
        )}
      </div>
    );
  }

  if (phase === "upcoming") {
    return (
      <div className="tvContent">
        <span className="fs15 infoTextselected">
          This match is scheduled and will begin soon.
        </span>
      </div>
    );
  }

  if (phase === "ended") {
    return (
      <div className="tvContent">
        {hasLiveScores(game) ? (
          <>
            {renderScoreLine("—", "—")}
            <span className="fs13 infoText mt-2 d-block">Final</span>
          </>
        ) : (
          <span className="fs15 infoTextselected">This match has ended.</span>
        )}
      </div>
    );
  }

  return (
    <div className="tvContent">
      <span className="fs15 infoText">Match status unavailable.</span>
    </div>
  );
}
